import { type FC, useRef } from "react";
import { AnimatedTooltip } from "widgets";
import { useTooltip } from "widgets/hooks";
import { colors } from "widgets/utils";
import { shallow, useShallow } from "zustand/shallow";

import { useGenomeViewerStore } from "../../store/store.ts";
import { AnchorPoint } from "./AnchorPoint.tsx";
import { useGroupWidth } from "./hooks/use-group-width.ts";
import { useGroupPosition } from "./hooks/use-groups-position.ts";
import { MIN_TRACKGROUP_WIDTH, SVG_STYLES } from "./settings.ts";

interface TrackGroupItemProps {
  idx: number;
  groupId: string;
  groupHeight: number;
  scale: number;
  panelWidth: number;
}

export const TrackGroupItem: FC<TrackGroupItemProps> = ({ idx, groupId, groupHeight, scale, panelWidth }) => {
  const ref = useRef<SVGRectElement | null>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(ref, { closeDelay: 0 });

  // trigger a render if the scroll changes (but not the asynchronous scrolling) or if an alignment is performed
  useGenomeViewerStore((state) => state.forceOverviewRenderFlag);

  const { organismName, contigName, baseShift, isVisible } = useGenomeViewerStore(
    useShallow((state) => {
      const trackGroup = state.trackGroups.get(groupId)!;
      return {
        organismName: trackGroup.organismName,
        contigName: trackGroup.contigName,
        baseShift: trackGroup.graphics.baseShift,
        isVisible: trackGroup.graphics.isVisible,
      };
    }),
  );
  const isGroupHovered = useGenomeViewerStore((state) => state.hoveredGroupId === groupId, shallow);

  const width = useGroupWidth(groupId, scale);
  const { x, y } = useGroupPosition(idx, scale, groupHeight, baseShift, isVisible, isGroupHovered);

  return (
    <g>
      {isGroupHovered && (
        <rect
          x={(SVG_STYLES.group.strokeWidth / 2) % 2}
          y={y + (SVG_STYLES.group.strokeWidth % 2)}
          width={panelWidth}
          height={groupHeight - (SVG_STYLES.group.strokeWidth % 2)}
          {...SVG_STYLES.hoverGroup}
          fill={colors.primaryPale + "6F"}
        />
      )}
      <g
        transform={`translate(${Math.min(x, panelWidth - MIN_TRACKGROUP_WIDTH - SVG_STYLES.group.strokeWidth)} ${y})`}
        data-testid={`contig-overview-${groupId}`}
      >
        <rect
          x={(SVG_STYLES.group.strokeWidth / 2) % 2}
          y={(SVG_STYLES.group.strokeWidth / 2) % 2}
          width={Math.round(width)}
          height={groupHeight}
          {...SVG_STYLES.group}
          ref={ref}
          {...triggerProps}
          fill={isGroupHovered ? colors.primaryPale + "6F" : SVG_STYLES.group.fill}
        />
        <AnimatedTooltip
          state={state}
          domProps={tooltipProps}
          targetRef={ref}
          title={organismName}
          placement="left"
          offset={5}
          nonInteractive
        >
          {contigName}
        </AnimatedTooltip>
        <AnchorPoint groupId={groupId} scale={scale} />
      </g>
    </g>
  );
};
